import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ChevronRight,
  Clock3,
  FlaskConical,
  Link as LinkIcon,
  Search,
  ShieldCheck,
  SignalHigh,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  adminApi,
  type ProcessingIncidentFilters,
  type ProcessingIncidentOperation,
  type ProcessingIncidentPriority,
  type ProcessingIncidentRow,
  type ProcessingIncidentStatus,
  type ProcessingIncidentSurface,
} from "@/services/adminApi";
import { SupportTriageTabs } from "@/components/support/SupportTriageTabs";
import {
  INCIDENT_SURFACES,
  SURFACE_OPERATIONS,
  SurfaceChip,
  TestBadge,
  operationLabel,
  surfaceLabel,
} from "@/components/support/incidentMeta";

const STATUSES: ProcessingIncidentStatus[] = [
  "OPEN",
  "TRIAGED",
  "LINKED_TO_ISSUE",
  "RESOLVED",
  "IGNORED",
];
const PRIORITIES: ProcessingIncidentPriority[] = ["HIGH", "URGENT"];

const STATUS_META: Record<
  ProcessingIncidentStatus,
  { label: string; dot: string; chip: string }
> = {
  OPEN: { label: "Open", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 ring-amber-200/60" },
  TRIAGED: { label: "Triaged", dot: "bg-sky-500", chip: "bg-sky-50 text-sky-700 ring-sky-200/60" },
  LINKED_TO_ISSUE: { label: "Linked", dot: "bg-violet-500", chip: "bg-violet-50 text-violet-700 ring-violet-200/60" },
  RESOLVED: { label: "Resolved", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200/60" },
  IGNORED: { label: "Ignored", dot: "bg-zinc-400", chip: "bg-zinc-50 text-zinc-600 ring-zinc-200/60" },
};

const PRIORITY_META: Record<
  ProcessingIncidentPriority,
  { label: string; chip: string; icon: LucideIcon }
> = {
  HIGH: { label: "High", chip: "bg-orange-50 text-orange-700 ring-orange-200/60", icon: ShieldCheck },
  URGENT: { label: "Urgent", chip: "bg-red-50 text-red-700 ring-red-200/70", icon: SignalHigh },
};

export default function SupportIncidentsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProcessingIncidentRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    status: ProcessingIncidentStatus | "";
    priority: ProcessingIncidentPriority | "";
    incidentSurface: ProcessingIncidentSurface | "";
    incidentOperation: ProcessingIncidentOperation | "";
    isTest: "true" | "false" | "";
    from: string;
    to: string;
  }>({ status: "", priority: "", incidentSurface: "", incidentOperation: "", isTest: "", from: "", to: "" });
  const [search, setSearch] = useState("");
  // Debounced server-side search (`q`): the server searches across ALL pages
  // (error summary + affected user/org ids), so matches beyond the loaded page
  // are found — unlike a client-only filter over already-loaded rows.
  const [appliedSearch, setAppliedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const hasFilters =
    Boolean(filters.status) ||
    Boolean(filters.priority) ||
    Boolean(filters.incidentSurface) ||
    Boolean(filters.incidentOperation) ||
    Boolean(filters.isTest) ||
    Boolean(filters.from) ||
    Boolean(filters.to);

  // Superadmin-only test trigger (server gates authorization): create a synthetic,
  // isolated incident through the real pipeline for PM validation.
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [triggerSurface, setTriggerSurface] = useState<ProcessingIncidentSurface>("settings");
  const [triggerOperation, setTriggerOperation] =
    useState<ProcessingIncidentOperation>("settings.save_failed");
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);

  const load = useCallback(
    async (cursor?: string | null) => {
      if (cursor) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const params: ProcessingIncidentFilters = { take: 50 };
        if (filters.status) params.status = filters.status;
        if (filters.priority) params.priority = filters.priority;
        if (filters.incidentSurface) params.incidentSurface = filters.incidentSurface;
        if (filters.incidentOperation) params.incidentOperation = filters.incidentOperation;
        if (filters.isTest) params.isTest = filters.isTest;
        if (filters.from) params.from = filters.from;
        if (filters.to) params.to = filters.to;
        if (appliedSearch) params.q = appliedSearch;
        if (cursor) params.cursor = cursor;
        const result = await adminApi.getProcessingIncidents(params);
        setRows((current) =>
          cursor ? [...current, ...result.incidents] : result.incidents,
        );
        setNextCursor(result.nextCursor);
      } catch (e: any) {
        setError(e?.response?.data?.error ?? "Failed to load processing incidents");
        if (!cursor) {
          setRows([]);
          setNextCursor(null);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filters, appliedSearch],
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const runTestTrigger = useCallback(async () => {
    setTriggering(true);
    setTriggerError(null);
    try {
      await adminApi.triggerTestProcessingIncident({
        incidentSurface: triggerSurface,
        incidentOperation: triggerOperation,
      });
      setTriggerOpen(false);
      await load(null);
    } catch (e: any) {
      setTriggerError(e?.response?.data?.error ?? "Failed to trigger test incident");
    } finally {
      setTriggering(false);
    }
  }, [triggerSurface, triggerOperation, load]);

  const counts = useMemo(() => {
    return {
      open: rows.filter((r) => r.status === "OPEN").length,
      urgent: rows.filter((r) => r.priority === "URGENT").length,
      review: rows.filter((r) => r.reviewSignalAt).length,
      total: rows.length,
    };
  }, [rows]);

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      <div className="mx-auto max-w-6xl px-8 py-10">
        <header className="mb-8 flex items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-500">
              <span className="inline-flex h-1 w-1 rounded-full bg-red-500" />
              Internal triage
            </div>
            <h1 className="mt-2 text-[28px] font-semibold tracking-[-0.02em] text-zinc-900">
              Processing incidents
            </h1>
            <p className="mt-1 text-[13.5px] text-zinc-500">
              Deduped platform-wide processing failures captured from Libra.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Stat label="Open" value={counts.open} accent={counts.open > 0} />
            <Stat label="Urgent" value={counts.urgent} accent={counts.urgent > 0} />
            <Stat label="Needs review" value={counts.review} accent={counts.review > 0} />
            <Stat label="Loaded" value={counts.total} />
          </div>
        </header>

        <SupportTriageTabs />

        <div className="mb-4 flex items-center justify-end">
          <button
            onClick={() => {
              setTriggerError(null);
              setTriggerOpen((open) => !open);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200/80 bg-white px-3 py-1.5 text-[12.5px] font-medium text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-800"
          >
            <FlaskConical className="h-3.5 w-3.5 text-zinc-400" />
            Trigger test incident
          </button>
        </div>

        {triggerOpen && (
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-400">
                Surface
              </label>
              <select
                value={triggerSurface}
                onChange={(e) => {
                  const surface = e.target.value as ProcessingIncidentSurface;
                  setTriggerSurface(surface);
                  setTriggerOperation(SURFACE_OPERATIONS[surface][0]);
                }}
                className="cursor-pointer rounded-lg border border-zinc-200/80 bg-white px-3 py-1.5 text-[13px] text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-200"
              >
                {INCIDENT_SURFACES.map((surface) => (
                  <option key={surface} value={surface}>
                    {surfaceLabel(surface)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-400">
                Operation
              </label>
              <select
                value={triggerOperation}
                onChange={(e) =>
                  setTriggerOperation(e.target.value as ProcessingIncidentOperation)
                }
                className="cursor-pointer rounded-lg border border-zinc-200/80 bg-white px-3 py-1.5 text-[13px] text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-200"
              >
                {SURFACE_OPERATIONS[triggerSurface].map((operation) => (
                  <option key={operation} value={operation}>
                    {operationLabel(operation)}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={runTestTrigger}
              disabled={triggering}
              className="rounded-lg bg-zinc-900 px-4 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-zinc-800 disabled:opacity-50"
            >
              {triggering ? "Creating…" : "Create test incident"}
            </button>
            {triggerError && (
              <span className="text-[12.5px] text-red-600">{triggerError}</span>
            )}
            <p className="w-full text-[11.5px] text-zinc-400">
              Runs the real pipeline but is marked as a test — isolated from real
              fingerprints, occurrence counts, and production escalation. Slack delivery
              is suppressed in production unless explicitly enabled.
            </p>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-200/80 bg-white p-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
          <div className="flex min-w-[260px] flex-1 items-center gap-2 px-3">
            <Search className="h-4 w-4 text-zinc-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by error, fingerprint, Linear issue, user, or org…"
              className="w-full bg-transparent text-[13.5px] text-zinc-800 placeholder:text-zinc-400 outline-none"
            />
          </div>
          <div className="flex items-center gap-1.5 border-l border-zinc-200/80 pl-3">
            <FilterSelect
              label="Status"
              value={filters.status}
              options={STATUSES.map((status) => ({
                value: status,
                label: STATUS_META[status].label,
              }))}
              onChange={(status) =>
                setFilters((current) => ({
                  ...current,
                  status: status as ProcessingIncidentStatus | "",
                }))
              }
            />
            <FilterSelect
              label="Priority"
              value={filters.priority}
              options={PRIORITIES.map((priority) => ({
                value: priority,
                label: PRIORITY_META[priority].label,
              }))}
              onChange={(priority) =>
                setFilters((current) => ({
                  ...current,
                  priority: priority as ProcessingIncidentPriority | "",
                }))
              }
            />
            <FilterSelect
              label="Surface"
              value={filters.incidentSurface}
              options={INCIDENT_SURFACES.map((surface) => ({
                value: surface,
                label: surfaceLabel(surface),
              }))}
              onChange={(surface) =>
                setFilters((current) => ({
                  ...current,
                  incidentSurface: surface as ProcessingIncidentSurface | "",
                  // Operation is scoped to the surface — clear it when the surface changes.
                  incidentOperation: "",
                }))
              }
            />
            {filters.incidentSurface && (
              <FilterSelect
                label="Operation"
                value={filters.incidentOperation}
                options={SURFACE_OPERATIONS[filters.incidentSurface].map((operation) => ({
                  value: operation,
                  label: operationLabel(operation),
                }))}
                onChange={(operation) =>
                  setFilters((current) => ({
                    ...current,
                    incidentOperation: operation as ProcessingIncidentOperation | "",
                  }))
                }
              />
            )}
            <FilterSelect
              label="Type"
              value={filters.isTest}
              options={[
                { value: "false", label: "Real" },
                { value: "true", label: "Test" },
              ]}
              onChange={(isTest) =>
                setFilters((current) => ({
                  ...current,
                  isTest: isTest as "true" | "false" | "",
                }))
              }
            />
            <input
              type="date"
              aria-label="Seen from"
              value={filters.from}
              onChange={(e) =>
                setFilters((current) => ({ ...current, from: e.target.value }))
              }
              className="cursor-pointer rounded-md bg-transparent px-2 py-1.5 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-100 focus:outline-none"
            />
            <span className="text-[12px] text-zinc-300">–</span>
            <input
              type="date"
              aria-label="Seen to"
              value={filters.to}
              onChange={(e) =>
                setFilters((current) => ({ ...current, to: e.target.value }))
              }
              className="cursor-pointer rounded-md bg-transparent px-2 py-1.5 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-100 focus:outline-none"
            />
            {hasFilters && (
              <button
                onClick={() =>
                  setFilters({
                    status: "",
                    priority: "",
                    incidentSurface: "",
                    incidentOperation: "",
                    isTest: "",
                    from: "",
                    to: "",
                  })
                }
                className="ml-1 rounded-md px-2 py-1 text-[12px] font-medium text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-20 animate-pulse rounded-xl bg-zinc-100/70" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-500">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <p className="text-[13.5px] text-zinc-600">{error}</p>
              <button
                onClick={() => load(null)}
                className="rounded-lg border border-zinc-200/80 bg-white px-3 py-1.5 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50"
              >
                Retry
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-20">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-50 text-zinc-400">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <p className="text-[13.5px] text-zinc-500">
                No processing incidents match your filters.
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-zinc-100">
                {rows.map((incident) => {
                  const status = STATUS_META[incident.status];
                  const priority = PRIORITY_META[incident.priority];
                  const PriorityIcon = priority.icon;
                  const needsReview = Boolean(incident.reviewSignalAt);
                  return (
                    <li key={incident.id}>
                      <button
                        onClick={() =>
                          navigate(`/admin/support/incidents/${incident.id}`)
                        }
                        className="group flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-[#fafaf9]"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600 ring-1 ring-red-100">
                          <AlertTriangle className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            {needsReview && (
                              <span className="relative flex h-2 w-2 shrink-0" title="New occurrence since review">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-500 opacity-50" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-orange-500" />
                              </span>
                            )}
                            <h3 className="truncate text-[14px] font-medium tracking-[-0.005em] text-zinc-900">
                              {incident.errorSummary}
                            </h3>
                            {incident.isTest && <TestBadge />}
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-zinc-500">
                            <SurfaceChip surface={incident.incidentSurface} />
                            <span className="font-mono text-[11px] text-zinc-500">
                              {operationLabel(incident.incidentOperation)}
                            </span>
                            <span className="text-zinc-300">·</span>
                            <span className="font-mono text-[11px] text-zinc-500">
                              {incident.method} {incident.endpoint}
                            </span>
                            {incident.errorName && (
                              <>
                                <span className="text-zinc-300">·</span>
                                <span>{incident.errorName}</span>
                              </>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11.5px] text-zinc-500">
                            <MiniStat
                              icon={<Clock3 className="h-3.5 w-3.5" />}
                              label={formatRelative(incident.lastSeenAt)}
                            />
                            <MiniStat
                              icon={<Clock3 className="h-3.5 w-3.5" />}
                              label={`first ${formatRelative(incident.firstSeenAt)}`}
                            />
                            <MiniStat
                              icon={<AlertTriangle className="h-3.5 w-3.5" />}
                              label={`${incident.occurrenceCount} occurrence${incident.occurrenceCount === 1 ? "" : "s"}`}
                            />
                            <MiniStat
                              icon={<Users className="h-3.5 w-3.5" />}
                              label={`${incident.affectedUserCount} users · ${incident.affectedOrganizationCount} orgs`}
                            />
                            {incident.externalIssueIdentifier && (
                              <MiniStat
                                icon={<LinkIcon className="h-3.5 w-3.5" />}
                                label={incident.externalIssueIdentifier}
                              />
                            )}
                            {incident.slackNotifyError && (
                              <span
                                className="inline-flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-0.5 text-red-600 ring-1 ring-inset ring-red-200/60"
                                title={incident.slackNotifyError}
                              >
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Slack failed
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="hidden items-center gap-2 md:flex">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset ${priority.chip}`}>
                            <PriorityIcon className="h-3 w-3" />
                            {priority.label}
                          </span>
                          {needsReview && (
                            <span className="inline-flex items-center rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-medium text-orange-700 ring-1 ring-inset ring-orange-200/70">
                              +{incident.reviewSignalCount}
                            </span>
                          )}
                        </div>
                        <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset ${status.chip}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                          {status.label}
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-zinc-300 transition-transform group-hover:translate-x-0.5 group-hover:text-zinc-500" />
                      </button>
                    </li>
                  );
                })}
              </ul>
              {nextCursor && (
                <div className="border-t border-zinc-100 p-3 text-center">
                  <button
                    onClick={() => load(nextCursor)}
                    disabled={loadingMore}
                    className="inline-flex items-center justify-center rounded-lg border border-zinc-200/80 bg-white px-4 py-2 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50"
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div className={`flex items-baseline gap-2 rounded-xl px-3.5 py-2 ring-1 ring-inset ${accent ? "bg-red-50 ring-red-200/70" : "bg-white ring-zinc-200/70"}`}>
      <span className={`text-[18px] font-semibold tabular-nums tracking-tight ${accent ? "text-red-700" : "text-zinc-900"}`}>
        {value}
      </span>
      <span className={`text-[11.5px] font-medium ${accent ? "text-red-700/80" : "text-zinc-500"}`}>
        {label}
      </span>
    </div>
  );
}

function MiniStat({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-zinc-50 px-1.5 py-0.5 text-zinc-500 ring-1 ring-inset ring-zinc-200/60">
      {icon}
      {label}
    </span>
  );
}

function FilterSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="cursor-pointer appearance-none rounded-md bg-transparent py-1.5 pl-2.5 pr-7 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-100 focus:outline-none"
      >
        <option value="">{label}: All</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {label}: {option.label}
          </option>
        ))}
      </select>
      <ChevronRight className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rotate-90 text-zinc-400" />
    </div>
  );
}

function formatRelative(value: string) {
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return "just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
